// src/components/Terminal.js
import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';

const blink = keyframes`
  0%, 49% { opacity: 1; }
  50%, 100% { opacity: 0; }
`;

const Container = styled.div`
  background-color: ${({ theme }) => theme.colors.terminal};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  overflow: hidden;
  box-shadow: ${({ theme }) => theme.shadows.strong};
  font-family: ${({ theme }) => theme.fonts.code};
  width: 100%;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  background-color: ${({ theme }) => theme.colors.backgroundLighter};
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
  position: relative;
`;

const Buttons = styled.div`
  display: flex;
  gap: 6px;
`;

const Button = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${({ color }) => color};
`;

const Title = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  font-size: ${({ theme }) => theme.fonts.sizes.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-weight: 500;
`;

const Content = styled.div`
  padding: ${({ theme }) => theme.spacing[4]};
  min-height: 150px;
  max-height: 400px;
  overflow-y: auto;
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: ${({ theme }) => theme.fonts.sizes.sm};
`;

const Line = styled.div`
  line-height: 1.5;
  margin-bottom: ${({ theme }) => theme.spacing[2]};
  
  &:last-child {
    display: flex;
    align-items: center;
  }
`;

const Prompt = styled.span`
  color: ${({ theme }) => theme.colors.primary};
  margin-right: ${({ theme }) => theme.spacing[2]};
`;

const Cursor = styled.span`
  display: inline-block;
  width: 8px;
  height: 16px;
  background-color: ${({ theme }) => theme.colors.primary};
  margin-left: 2px;
  animation: ${blink} 1s infinite;
`;

const Terminal = ({ commands = [], showPrompt = true, title = "C.H.I.E.F TERMINAL" }) => {
    const [displayedLines, setDisplayedLines] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const contentRef = useRef(null);

    useEffect(() => {
        if (commands.length === 0 || currentIndex >= commands.length) return;

        const timer = setTimeout(() => {
            setDisplayedLines(prev => [...prev, commands[currentIndex].text]);
            setCurrentIndex(currentIndex + 1);

            // Scroll to bottom
            if (contentRef.current) {
                contentRef.current.scrollTop = contentRef.current.scrollHeight;
            }
        }, commands[currentIndex].delay || 1000);

        return () => clearTimeout(timer);
    }, [commands, currentIndex]);

    return (
        <Container>
            <Header>
                <Buttons>
                    <Button color="#FF5F56" />
                    <Button color="#FFBD2E" />
                    <Button color="#27C93F" />
                </Buttons>
                <Title>{title}</Title>
            </Header>

            <Content ref={contentRef}>
                {displayedLines.map((line, index) => (
                    <Line key={index}>
                        {index > 0 && showPrompt && <Prompt>$</Prompt>}
                        {line}
                    </Line>
                ))}

                {(commands.length === 0 || currentIndex >= commands.length) && (
                    <Line>
                        <Prompt>$</Prompt>
                        <Cursor />
                    </Line>
                )}
            </Content>
        </Container>
    );
};

export default Terminal;